import axios from '@/libs/axios';
import { PROFILE_KEY } from '@/libs/acl/config';
import { $themeConfig as legacyThemeConfig } from '@themeConfig';
import { $themeConfig as newThemeConfig } from '@/libs/themeConfigNew';
import {
  DEFAULT_IS_HESBA_CLIENT,
  getTenantSubdomainFromHost,
  isHesbaHost,
} from '@/libs/appHost';

export const LAST_IS_HESBA_CLIENT_KEY = 'plugin-last-is-hesba-client';
export const TENANT_DESIGN_CACHE_PREFIX = 'plugin-tenant-design:';

/**
 * PRE-LOGIN DESIGN (Login / ForgotPassword)
 * -----------------------------------------
 * Before auth, login UI follows the site domain:
 *   *.hesbaksa.com → new (حسبة) design
 *   *.plugin-erp.com tenant subdomain → GET Tenants/design?domainName={subdomain}
 *
 * After successful login, profile.isHesbaClient from the auth response still wins.
 */

let resolvedPreLoginIsHesbaClient = null;
let resolvePreLoginDesignPromise = null;

let newDesignStylesPromise = null;

function loadNewDesignStyles() {
  if (!newDesignStylesPromise) {
    newDesignStylesPromise = import('@/assets/scss/design-new-bundle.scss').then(
      () =>
        new Promise((resolve) => {
          // Wait two frames so extracted CSS is applied before first app paint
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        }),
    );
  }
  return newDesignStylesPromise;
}

function getThemeConfig(isHesbaClient) {
  return isHesbaClient ? newThemeConfig : legacyThemeConfig;
}

function getNewDesignTheme() {
  try {
    const skin = localStorage.getItem('vuexy-skin');
    return skin === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function syncThemeAttribute(isHesbaClient) {
  if (!isHesbaClient) {
    document.documentElement.removeAttribute('data-theme');
    return;
  }

  document.documentElement.setAttribute('data-theme', getNewDesignTheme());
}

function applyLayoutConfig(isHesbaClient, store) {
  if (!store) return;

  const layout = getThemeConfig(isHesbaClient).layout;

  store.commit('appConfig/UPDATE_NAVBAR_CONFIG', {
    type: layout.navbar.type,
    backgroundColor: layout.navbar.backgroundColor || '',
  });
}

function syncBrandChrome(isHesbaClient) {
  const enabled = !!isHesbaClient;
  document.title = enabled ? 'حسبة' : 'Plugin-Soft';

  const favicon = document.querySelector("link[rel='icon']");
  if (favicon) {
    favicon.setAttribute('href', enabled ? '/hasba-icon.png' : '/tap.png');
  }

  const splash = document.querySelector('#loading-bg .loading-logo img');
  if (splash) {
    splash.setAttribute('src', enabled ? '/hasba-logo.png' : '/loading.png');
    splash.setAttribute('alt', enabled ? 'حسبة' : 'Logo');
  }
}

export function applyDesignFlag(isHesbaClient, store) {
  const enabled = !!isHesbaClient;

  document.documentElement.setAttribute(
    'data-design',
    enabled ? 'new' : 'legacy',
  );

  try {
    localStorage.setItem(LAST_IS_HESBA_CLIENT_KEY, enabled ? '1' : '0');
  } catch {
    // ignore storage errors
  }

  syncThemeAttribute(enabled);
  syncBrandChrome(enabled);
  applyLayoutConfig(enabled, store);

  if (enabled && store) {
    try {
      if (!localStorage.getItem('vuexy-skin')) {
        store.commit('appConfig/UPDATE_SKIN', 'light');
      }
    } catch {
      // ignore storage errors
    }
  }

  if (enabled) {
    // Await so callers can avoid painting legacy UI before Hasba CSS lands
    return loadNewDesignStyles();
  }

  return Promise.resolve();
}

export function readCachedIsHesbaClient() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (raw) {
      const profile = JSON.parse(raw);
      return !!profile.isHesbaClient;
    }
    return localStorage.getItem(LAST_IS_HESBA_CLIENT_KEY) === '1';
  } catch {
    return false;
  }
}

function isIpOrLocalHost(host) {
  if (!host) return true;
  if (host === 'localhost') return true;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return true;
  if (host.includes(':')) return true;
  return false;
}

export function getTenantDomainNameFromUrl() {
  const host = window.location.hostname;
  if (isIpOrLocalHost(host)) {
    return process.env.VUE_APP_TENANT_DOMAIN || null;
  }

  const tenant = getTenantSubdomainFromHost(host);
  if (tenant) return tenant;

  return process.env.VUE_APP_TENANT_DOMAIN || null;
}

function getTenantDesignCacheKey(domainName) {
  return `${TENANT_DESIGN_CACHE_PREFIX}${domainName}`;
}

export function readTenantDesignCache(domainName) {
  if (!domainName) return null;

  try {
    const raw = sessionStorage.getItem(getTenantDesignCacheKey(domainName));
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // ignore storage errors
  }

  return null;
}

export function writeTenantDesignCache(domainName, isHesbaClient) {
  if (!domainName) return;

  try {
    sessionStorage.setItem(
      getTenantDesignCacheKey(domainName),
      isHesbaClient ? '1' : '0',
    );
  } catch {
    // ignore storage errors
  }
}

export async function fetchTenantDesignByDomain(domainName) {
  if (!domainName) return false;

  try {
    const { data } = await axios.get(
      `Tenants/design?domainName=${encodeURIComponent(domainName)}`,
    );
    return !!data.isHesbaClient;
  } catch {
    return false;
  }
}

export function readResolvedIsHesbaClient() {
  if (resolvedPreLoginIsHesbaClient !== null) {
    return resolvedPreLoginIsHesbaClient;
  }

  if (isHesbaHost() && !hasAuthenticatedProfile()) {
    return true;
  }

  const domainName = getTenantDomainNameFromUrl();
  if (domainName) {
    const cached = readTenantDesignCache(domainName);
    if (cached !== null) return cached;
  }

  return readCachedIsHesbaClient();
}

function hasAuthenticatedProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return false;
    const profile = JSON.parse(raw);
    return !!(profile && (profile.userId || profile.userName));
  } catch {
    return false;
  }
}

export async function resolvePreLoginDesign(store) {
  if (resolvePreLoginDesignPromise) {
    return resolvePreLoginDesignPromise;
  }

  resolvePreLoginDesignPromise = (async () => {
    const domainName = getTenantDomainNameFromUrl();
    let isHesbaClient;

    if (isHesbaHost()) {
      isHesbaClient = true;
    } else if (domainName) {
      isHesbaClient = await fetchTenantDesignByDomain(domainName);
      writeTenantDesignCache(domainName, isHesbaClient);
    } else {
      isHesbaClient = DEFAULT_IS_HESBA_CLIENT;
    }

    resolvedPreLoginIsHesbaClient = isHesbaClient;
    await applyDesignFlag(isHesbaClient, store);
    return isHesbaClient;
  })();

  return resolvePreLoginDesignPromise;
}

export function installDesignStoreHooks(store) {
  if (installDesignStoreHooks._done) return;
  installDesignStoreHooks._done = true;

  const original = store._mutations['appConfig/UPDATE_SKIN']?.[0];
  if (!original) return;

  store._mutations['appConfig/UPDATE_SKIN'][0] = (state, skin) => {
    original(state, skin);
    if (document.documentElement.getAttribute('data-design') === 'new') {
      syncThemeAttribute(true);
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  };
}

export function getActiveThemeConfig(isHesbaClient) {
  return getThemeConfig(!!isHesbaClient);
}
